import type { FC } from 'react'
import { useEffect, useState, useContext } from 'react'

import Loading from '@/components/Loading'
import type { OverrideOutcomeFieldConfig } from '@/components/OverrideOutcomeFields/OverrideOutcomeFields'
import OverrideOutcomeFields, {
  OverrideHeader,
} from '@/components/OverrideOutcomeFields/OverrideOutcomeFields'
import ctx from '@/context/AppContext'
import { useApplicationOutcomeQuery, useOverrideOutcomeMutation } from '@/lib/apollo'
import type { Outcome, ResidentialOutcome } from '@/lib/apollo/model/outcome'
import {
  OutcomeSectionName,
  ResidentialOutcomePropertyName,
} from '@/lib/apollo/model/outcome'
import { OutcomeStatus } from '@/types/Outcome'
import type { OutcomeOverrideSection } from '@/types/OutcomeOverride'
import {
  mapToGraphQLOverrides,
  outcomeStatusToLabel,
  overallSectionOverride,
  shouldBeOverridden,
} from '@/utils/outcomeOverrides'

const updatePropertyOutcomeStatus = (
  outcomeSection: ResidentialOutcome,
  propertyName: ResidentialOutcomePropertyName,
  outcomeStatus: OutcomeStatus,
): ResidentialOutcome => ({
  ...outcomeSection,
  properties: outcomeSection.properties.map(p =>
    p.name === propertyName
      ? {
          ...p,
          status: outcomeStatus,
        }
      : p,
  ),
})

const getFieldConfig = (
  hasLandlordReference: boolean,
): OverrideOutcomeFieldConfig<ResidentialOutcome>[] => {
  const landlordOutcome: OverrideOutcomeFieldConfig<ResidentialOutcome> = {
    label: 'Landlord reference',
    getStatus: outcomeSection =>
      getPropertyStatusFromOutcome(
        outcomeSection,
        ResidentialOutcomePropertyName.addressReference,
      ),
    applyOutcomeStatus: (outcomeSection, outcomeStatus) =>
      updatePropertyOutcomeStatus(
        outcomeSection,
        ResidentialOutcomePropertyName.addressReference,
        outcomeStatus,
      ),
    options: [
      {
        label: outcomeStatusToLabel(OutcomeStatus.rejected),
        status: OutcomeStatus.rejected,
      },
      {
        label: outcomeStatusToLabel(OutcomeStatus.conditionalPass),
        status: OutcomeStatus.conditionalPass,
      },
      {
        label: outcomeStatusToLabel(OutcomeStatus.accepted),
        status: OutcomeStatus.accepted,
      },
      {
        label: outcomeStatusToLabel(OutcomeStatus.awaitingReview),
        status: OutcomeStatus.awaitingReview,
        disabled: true,
      },
      {
        label: outcomeStatusToLabel(OutcomeStatus.awaitingResponse),
        status: OutcomeStatus.awaitingResponse,
        disabled: true,
      },
      {
        label: 'Auto pass',
        status: OutcomeStatus.pass,
        disabled: true,
      },
    ],
  }

  return [
    {
      label: <strong>Residential Section</strong>,
      getStatus: outcomeSection => outcomeSection.status,
      applyOutcomeStatus: (outcomeSection, outcomeStatus) => ({
        ...outcomeSection,
        status: outcomeStatus,
      }),
      options: overallSectionOverride,
    },
    ...(hasLandlordReference ? [landlordOutcome] : []),
  ]
}

const getPropertyStatusFromOutcome = (
  outcomeSection: ResidentialOutcome,
  propertyName: ResidentialOutcomePropertyName,
) => {
  const status = outcomeSection.properties.find(
    ({ name }) => name === propertyName,
  )?.status

  return status
}

const toOutcomeOverrides = (outcome: ResidentialOutcome): OutcomeOverrideSection[] => {
  const addressReference = getPropertyStatusFromOutcome(
    outcome,
    ResidentialOutcomePropertyName.addressReference,
  )

  const overriddenOutcomeStatus =
    outcome.status === OutcomeStatus.reOpened ? OutcomeStatus.fail : outcome.status

  return [
    {
      name: OutcomeSectionName.residential,
      status: overriddenOutcomeStatus,
      properties: [
        ...(shouldBeOverridden(addressReference)
          ? [
              {
                name: ResidentialOutcomePropertyName.addressReference,
                status: addressReference,
              },
            ]
          : []),
      ],
    } as ResidentialOutcome,
  ]
}

interface Props {
  customerId: string
  applicationId: string
  subjectId: string
  subjectOutcome?: Outcome
  hasLandlordReference: boolean
}

const OverrideOutcomes: FC<Props> = ({
  customerId,
  applicationId,
  subjectId,
  subjectOutcome,
  hasLandlordReference,
}) => {
  const [outcome, setOutcome] = useState<ResidentialOutcome | undefined>(undefined)
  const [isSuccess, setIsSuccess] = useState<boolean>(false)
  const [submitError, setSubmitError] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const [saveOutcomeOverrides] = useOverrideOutcomeMutation()

  const { personId } = useContext(ctx)

  const userId = personId ?? ''

  const { loading, error, data } = useApplicationOutcomeQuery({
    variables: {
      customerId,
      applicationId,
      subjectId,
      isReport: false,
    },
    fetchPolicy: 'network-only',
  })

  useEffect(() => {
    const outcome = data?.getDashboardApplication.outcome ?? undefined

    const residentialSectionOutcome = outcome?.sections?.find(
      ({ name }) => name === OutcomeSectionName.residential,
    ) as ResidentialOutcome

    setOutcome(residentialSectionOutcome)
  }, [data])

  const handleSubmit = async (reason: string) => {
    setIsSuccess(false)
    setSubmitError(false)
    setIsLoading(false)

    if (outcome) {
      const outcomeOverrides = mapToGraphQLOverrides(toOutcomeOverrides(outcome))

      saveOutcomeOverrides({
        variables: {
          customerId,
          applicationId,
          subjectId,
          outcomeOverrides,
          userId,
          reason,
        },
      })
        .then(a => {
          if (a.data?.overrideOutcome === 'Successfully overrode outcome') {
            setIsSuccess(true)
          }
        })
        .catch(() => {
          setSubmitError(true)
        })
        .finally(() => setIsLoading(false))
    }
  }

  return (
    <>
      <OverrideHeader>Review subsections</OverrideHeader>
      {outcome === undefined ? (
        <>There is nothing to review.</>
      ) : error !== undefined ? (
        <>An unexpected error occurred</>
      ) : loading ? (
        <Loading />
      ) : (
        <OverrideOutcomeFields<ResidentialOutcome>
          subjectOutcome={subjectOutcome}
          fieldConfig={getFieldConfig(hasLandlordReference)}
          outcomeSection={outcome}
          onChangeOutcomeSection={setOutcome}
          handleSubmit={handleSubmit}
          isError={submitError}
          isLoading={isLoading}
          isSuccess={isSuccess}
        />
      )}
    </>
  )
}

export default OverrideOutcomes
